import { Button, FormControl, Link, Stack, useDisclosure } from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { InitialGeneralParamsValues, InitialVehicleValues } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { DenniPovVehicleValidationSchema } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { Apis } from '@/appSettings'
import { CustomWaypoint } from '@/components/CustomWaypoint'
import { DocumentIcon } from '@/components/Documents/DocumentIcon'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { FormSection } from '@/containers/forms/FormSection'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSidebar } from '@/context/SidebarContext'
import { useUrl } from '@/hooks'
import { useSaveStep1 } from '@/hooks/useWizard'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { GeneralParams } from './containers/GeneralParams'
import { Vehicle } from './containers/Vehicle'

export const schema = yup.object().shape(
  {
    vehicle: DenniPovVehicleValidationSchema
  }
) as any

export type Step1FormValues = Omit<InsuranceParamsDto, 'baseParams' | 'policyHolder'> // base params je az na K2

export type Step1FormProps = {
  data?: Step1FormValues
}

const defaultValues: Step1FormValues = {
  nextPage: 'next',
  vehicle: InitialVehicleValues,
  generalParams: InitialGeneralParamsValues
}

export const Step1 = ({ data = defaultValues }: Step1FormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { triggerNewInsurance, clearTriggerNewInsurance, refetchInsuranceParams, insuranceParams, insuranceParamsId: paramsId, isContracted } = useInsuranceParams()
  const navigate = useNavigate()

  const validationSummaryRef = useRef<HTMLDivElement>(null)

  const defaultFormData = { ...data }

  if (defaultFormData.generalParams && !defaultFormData.generalParams.clientId) {
    defaultFormData.generalParams.clientId = global.newUid()
  }

  const methods = useForm<Step1FormValues>({
    values: defaultFormData,
    resolver: yupResolver(schema),
    mode: 'onBlur'
  })

  const {
    formState: { errors, dirtyFields },
    handleSubmit,
    reset,
    setError,
    getValues
  } = methods

  const { mutateAsync: saveStep1 } = useSaveStep1()

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  // v nevalidniho formulare se nascroluje na validationSummary
  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(`${Product.VehicleInsurance}-short`, 1, paramsId, methods)

  const submitForm = useCallback(handleSubmit((data) => {
    setIsLoading(true)
    const values = cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.generalParams.insuranceStartDate = formatDate(values.generalParams.insuranceStartDate)
    values.generalParams.insuranceEndDate = formatDate(values.generalParams.insuranceEndDate)
    values.vehicle.firstRegistrationDate = formatDate(values.vehicle.firstRegistrationDate)

    // ten jejich RadioIcon a select neumi s intem
    values.vehicle.druh = values.vehicle.druh ? parseInt(values.vehicle.druh.toString()) as any : undefined
    values.vehicle.palivo = values.vehicle.palivo ? parseInt(values.vehicle.palivo.toString()) as any : undefined

    return saveStep1(values as InsuranceParamsDto)
      .then(async (response) => {
        data.generalParams.paramsId = response.data?.toString()
        reset({ ...data })

        insuranceParamsFormPersistance.clear()

        if (paramsId && paramsId == data.generalParams.paramsId) {
          await refetchInsuranceParams()
          savedFormAlertOnOpen()
          setIsLoading(false)
        }
        else if (data.generalParams.paramsId) {
          // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
          setTimeout(() => {
            navigate(`/shortInsurance/${data.generalParams.paramsId}`)
            setIsLoading(false)
          }, 100)
        }
      })
      .catch((err) => {
        setIsLoading(false)

        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
  }, () => {
    scrollToValidationSummary()
  }), [handleSubmit])

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    // debugger // eslint-disable-line no-debugger
    if (triggerNewInsurance) {
      reset({
        ...defaultValues,
        generalParams: {
          ...defaultValues.generalParams,
          clientId: global.newUid() // je jeste potreba nastavit nove clientId
        }
      })

      clearTriggerNewInsurance()
    }
  }, [triggerNewInsurance])

  const url = useUrl(Apis.document)

  const { pageSections, setPageSections } = useSidebar()
  useEffect(() => {
    let i = 1
    const array: Section[] = [
      {
        id: 'information',
        sectionTitle: 'Obecné informace',
        index: i++,
        children: <GeneralParams />
      },
      {
        id: 'vozidlo',
        sectionTitle: 'Vozidlo',
        index: i++,
        children: <Vehicle
          isLoading={isLoading}
          onSave={submitForm}
          isContracted={isContracted}
        />,
      },
    ]

    if (insuranceParams?.generalParams.paramsId && isContracted) {
      array.push({
        id: 'documents',
        sectionTitle: 'Dokumenty',
        index: i,
        children: (
          <FormSection number={i++} sectionTitle="Dokumenty">
            <FormBody>
              <FormRow>
                <FormControl>
                  <DocumentIcon documentType='pdf' />
                  <Button as={Link} href={`${url}/GetShortInsuranceGreenCard?id=${insuranceParams?.generalParams.paramsId}`} target='_blank' variant="link">Zelená karta</Button>
                </FormControl>
              </FormRow>

            </FormBody>
          </FormSection>
        ),
      },)
    }

    setPageSections(array)
  }, [isLoading, submitForm, insuranceParams?.generalParams.paramsId])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Stack gap={8}>
          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          <ErrorSummary errors={errors} ref={validationSummaryRef} />

          <UnsavedFormModal {...insuranceParamsFormPersistance} />

          <SubmitDialog
            isOpen={isSavedFormAlertOpen}
            onClose={savedFormAlertOnClose}
            header='Uloženo'
            body='Změny byly úspěšně uloženy'
          />
        </Stack>
      </form>
    </FormProvider>
  )
}
